import { useEffect, useState } from 'react';
import { Badge, Group, Button, Menu, ActionIcon, Text, Stack, Alert, Modal, TextInput, SegmentedControl } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { MoreVertical, Plus, AlertTriangle, RefreshCw } from 'lucide-react';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useAuth } from '@/features/auth/AuthContext';
import { OperatorUsersViewModel } from './OperatorUsersViewModel';
import type { OperatorRole, OperatorUser } from './OperatorUsersService';

function fullName(u: OperatorUser): string {
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
}

async function act(p: Promise<void>, successMsg: string): Promise<void> {
    try {
        await p;
        notifications.show({ color: 'green', message: successMsg });
    } catch (e) {
        notifications.show({ color: 'red', title: 'Action failed', message: e instanceof Error ? e.message : 'Unexpected error' });
    }
}

export default function OperatorUsersPage(): React.ReactElement {
    const vm = useViewModel(OperatorUsersViewModel);
    const { user } = useAuth();
    const [inviteOpen, inviteCtl] = useDisclosure(false);

    useEffect(() => { void vm.load(); }, [vm]);

    const columns: MegaGridColumn<OperatorUser>[] = [
        { key: 'email', header: 'Email', sortValue: (u) => u.email, render: (u) => <Text size="sm" fw={500}>{u.email}</Text> },
        { key: 'name', header: 'Name', sortValue: (u) => fullName(u), render: (u) => fullName(u) },
        {
            key: 'role', header: 'Role', sortValue: (u) => u.role,
            render: (u) => <Badge color={u.role === 'admin' ? 'grape' : 'blue'} variant="light" size="sm">{u.role === 'admin' ? 'Admin' : 'Billing'}</Badge>,
        },
        {
            key: 'status', header: 'Status', sortValue: (u) => (u.enabled ? 'active' : 'disabled'),
            render: (u) => <Badge color={u.enabled ? 'green' : 'gray'} variant="light" size="sm">{u.enabled ? 'active' : 'disabled'}</Badge>,
        },
        {
            key: 'createdAt', header: 'Created', sortValue: (u) => u.createdAt ?? 0,
            render: (u) => <Text size="sm" c="dimmed">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''}</Text>,
        },
        {
            key: 'actions', header: '', width: 56, align: 'right', noFilter: true,
            render: (u): React.ReactElement => {
                const isSelf = u.id === user?.id;
                return (
                    <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" loading={vm.isBusy(u.id)} aria-label="Actions">
                                <MoreVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            {u.enabled ? (
                                <Menu.Item disabled={isSelf} onClick={() => void act(vm.setEnabled(u.id, false), 'Operator disabled')}>Disable</Menu.Item>
                            ) : (
                                <Menu.Item onClick={() => void act(vm.setEnabled(u.id, true), 'Operator enabled')}>Enable</Menu.Item>
                            )}
                            <Menu.Divider />
                            <Menu.Item color="red" disabled={isSelf} onClick={() => void act(vm.remove(u.id), 'Operator deleted')}>Delete</Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                );
            },
        },
    ];

    return (
        <Stack gap="md">
            <PageHeaderComponent
                title="Operators"
                subtitle="Vendor staff with operator-console access — Admin (full console) or Billing (billing surfaces only). New operators enrol MFA on first sign-in."
                rightToolbar={
                    <Group gap="xs">
                        <Button variant="light" leftSection={<RefreshCw size={16} />} onClick={() => void vm.load()}>Refresh</Button>
                        <Button leftSection={<Plus size={16} />} onClick={inviteCtl.open}>Invite operator</Button>
                    </Group>
                }
            />

            {vm.error && <Alert color="red" icon={<AlertTriangle size={16} />}>{vm.error}</Alert>}

            <MegaGrid
                persistenceKey="operator-users"
                columns={columns}
                rows={vm.users ?? []}
                getRowKey={(u) => u.id}
                searchAccessor={(u) => `${u.email} ${fullName(u)}`}
                searchPlaceholder="Search operators"
                emptyMessage="No operators"
                loading={vm.loading}
                clientPageSize={25}
            />

            <InviteModal opened={inviteOpen} onClose={inviteCtl.close} vm={vm} />
        </Stack>
    );
}

function InviteModal({ opened, onClose, vm }: { opened: boolean; onClose: () => void; vm: OperatorUsersViewModel }): React.ReactElement {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [role, setRole] = useState<OperatorRole>('admin');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (): Promise<void> => {
        setSubmitting(true);
        try {
            await vm.invite({ email: email.trim(), role, ...(firstName.trim() ? { firstName: firstName.trim() } : {}), ...(lastName.trim() ? { lastName: lastName.trim() } : {}) });
            notifications.show({ color: 'green', message: `Invited ${email}` });
            setEmail(''); setFirstName(''); setLastName(''); setRole('admin');
            onClose();
        } catch (e) {
            notifications.show({ color: 'red', title: 'Invite failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Invite operator" centered>
            <Stack gap="sm">
                <TextInput label="Email" placeholder="operator@cloudsaver.com" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />
                <Group grow>
                    <TextInput label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
                    <TextInput label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
                </Group>
                <Stack gap={4}>
                    <Text size="sm" fw={500}>Role</Text>
                    <SegmentedControl
                        value={role}
                        onChange={(value) => setRole(value as OperatorRole)}
                        data={[{ label: 'Admin', value: 'admin' }, { label: 'Billing', value: 'billing' }]}
                        fullWidth
                    />
                    <Text size="xs" c="dimmed">
                        {role === 'admin' ? 'Full console access — tenants, operators, billing, and audit.' : 'Billing surfaces only — plans, billing, and settings.'}
                    </Text>
                </Stack>
                <Text size="xs" c="dimmed">They'll receive an email to set a password, and must enrol TOTP on first sign-in.</Text>
                <Group justify="flex-end" mt="sm">
                    <Button variant="subtle" onClick={onClose}>Cancel</Button>
                    <Button onClick={() => void submit()} loading={submitting} disabled={!email.trim()}>Invite</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
